"use client";

import { useTranslations } from "@packages/i18n";
import type { Images, Prayer } from "@packages/sdk";
import stylex from "@stylexjs/stylex";
import type { MouseEvent } from "react";

import { spacing } from "../../../../../../global/stylex/vars.stylex";
import { useFavoritePrayerAction } from "../../../../../hooks";
import type { WithStylexArray } from "../../../../../types";
import { AddToQueue } from "../../../../blocks";
import { IconButton } from "../../../_core";
import { MoreIcon, StarIcon } from "../../../icons";
import type { DropdownMenuProps } from "../../../modals";
import { DropdownMenu, DropdownMenuItem } from "../../../modals";
import ContentMoreMenuHeader from "./ContentMoreMenuHeader";

const styles = stylex.create({
  dropdownMenuContent: { paddingTop: spacing.l },
});

type PrayerMoreMenuProps = DropdownMenuProps &
  WithStylexArray<{
    imageSrc?: Images;
    prayer: Pick<
      Prayer,
      "id" | "is_favorite" | "title" | "label_desc" | "lengths" | "has_access"
    >;
  }>;

export const PrayerMoreMenu = ({
  prayer,
  imageSrc,
  ...props
}: PrayerMoreMenuProps) => {
  const updateFavorite = useFavoritePrayerAction({
    id: prayer.id,
    has_access: prayer.has_access,
  });
  const t = useTranslations();

  const onClickFavorite = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    updateFavorite();
  };

  return (
    <DropdownMenu
      align="end"
      contentStyleXArray={[styles.dropdownMenuContent]}
      modal={false}
      trigger={
        <IconButton
          icon={<MoreIcon />}
          onClick={(e) => {
            e.preventDefault();
          }}
          variant="transparentPrimary"
        />
      }
      {...props}
    >
      <ContentMoreMenuHeader
        imageSrc={imageSrc}
        metadata={prayer.lengths}
        subtitle={prayer.label_desc}
        title={prayer.title}
      />
      <DropdownMenuItem
        onClick={onClickFavorite}
        startIcon={prayer.is_favorite ? <StarIcon.Off /> : <StarIcon.Outline />}
      >
        {prayer.is_favorite
          ? t("dialog_prayer_options_unfavorite")
          : t("dialog_prayer_options_favorite")}
      </DropdownMenuItem>
      <AddToQueue id={prayer.id} type={"prayer"} />
    </DropdownMenu>
  );
};
